import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenantSettings } from '../hooks/useTenantSettings';
import { Clock, Plus, Pencil, Trash2, Truck, Users, FileText, ArrowRight, ChevronDown, ChevronUp, RefreshCw, User } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';

const ACTION_CONFIG = {
  created: { label: 'Created', icon: Plus, color: 'bg-green-500/20 text-green-400 border-green-500/40' },
  updated: { label: 'Updated', icon: Pencil, color: 'bg-blue-500/20 text-blue-400 border-blue-500/40' },
  deleted: { label: 'Deleted', icon: Trash2, color: 'bg-red-500/20 text-red-400 border-red-500/40' },
  allocation_added: { label: 'Asset Allocated', icon: Truck, color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40' },
  allocation_removed: { label: 'Asset Removed', icon: Truck, color: 'bg-orange-500/20 text-orange-400 border-orange-500/40' },
  allocation_updated: { label: 'Allocation Updated', icon: Truck, color: 'bg-blue-500/20 text-blue-400 border-blue-500/40' },
  worker_added: { label: 'Worker Assigned', icon: Users, color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40' },
  worker_removed: { label: 'Worker Removed', icon: Users, color: 'bg-orange-500/20 text-orange-400 border-orange-500/40' },
  worker_updated: { label: 'Worker Reassigned', icon: Users, color: 'bg-blue-500/20 text-blue-400 border-blue-500/40' },
  docket_created: { label: 'Docket Created', icon: FileText, color: 'bg-purple-500/20 text-purple-400 border-purple-500/40' },
  docket_submitted: { label: 'Docket Submitted', icon: FileText, color: 'bg-amber-500/20 text-amber-400 border-amber-500/40' },
  docket_approved: { label: 'Docket Approved', icon: FileText, color: 'bg-green-500/20 text-green-400 border-green-500/40' },
  docket_rejected: { label: 'Docket Rejected', icon: FileText, color: 'bg-red-500/20 text-red-400 border-red-500/40' },
  status_changed: { label: 'Status Changed', icon: ArrowRight, color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/40' },
};

function formatRelativeTime(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return `${diffDays}d ago`;
}

function ChangeRow({ change }) {
  const hasOld = change.old_value && change.old_value !== '';
  const hasNew = change.new_value && change.new_value !== '';

  return (
    <div className="py-1.5 px-3 border-l-2 border-border/50 ml-1" data-testid="audit-change-row">
      <div className="text-[11px] text-muted-foreground font-medium">{change.label || change.field}</div>
      <div className="flex items-center gap-2 text-xs mt-0.5 flex-wrap">
        {hasOld && (
          <span className="text-red-400 line-through">{change.old_value}</span>
        )}
        {hasOld && hasNew && (
          <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
        )}
        {hasNew && (
          <span className="text-emerald-400">{change.new_value}</span>
        )}
        {!hasOld && !hasNew && (
          <span className="text-muted-foreground italic">cleared</span>
        )}
      </div>
    </div>
  );
}

function AuditEntry({ entry, isLast, fmtDateTime }) {
  const config = ACTION_CONFIG[entry.action] || ACTION_CONFIG.updated;
  const Icon = config.icon;
  const [expanded, setExpanded] = useState(true);
  const hasChanges = entry.changes && entry.changes.length > 0;
  const manyChanges = hasChanges && entry.changes.length > 4;

  const displayName = entry.actor_name || 'Unknown user';
  const fullTimestamp = fmtDateTime ? fmtDateTime(entry.created_at) : entry.created_at;
  const relativeTime = formatRelativeTime(entry.created_at);

  return (
    <div className="relative flex gap-3" data-testid="audit-log-entry">
      {/* Timeline connector */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div className="w-2.5 h-2.5 rounded-full bg-muted-foreground/40 border-2 border-muted-foreground/60 mt-1.5 z-10" />
        {!isLast && <div className="w-px flex-1 bg-border mt-1" />}
      </div>

      {/* Content */}
      <div className="flex-1 pb-5 min-w-0">
        {/* Header */}
        <div className="flex items-center gap-x-2 gap-y-1 flex-wrap">
          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-5 gap-1 ${config.color}`}>
            <Icon className="h-3 w-3" />
            {config.label}
          </Badge>
          <span className="flex items-center gap-1 text-xs font-medium text-foreground" data-testid="audit-entry-actor">
            <User className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            {displayName}
          </span>
          <span className="flex items-center gap-1 text-[11px] text-muted-foreground" data-testid="audit-entry-timestamp">
            <Clock className="h-3 w-3 flex-shrink-0" />
            {fullTimestamp}
            <span className="opacity-60">({relativeTime})</span>
          </span>
        </div>

        {/* Changes */}
        {hasChanges && (
          <div className="mt-2 bg-muted/50 border border-border/50 rounded-md overflow-hidden">
            {manyChanges && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
              >
                <span>{entry.changes.length} fields changed</span>
                {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>
            )}
            {(expanded || !manyChanges) && (
              <div className="divide-y divide-border/30">
                {entry.changes.map((change, i) => (
                  <ChangeRow key={`${change.field}-${i}`} change={change} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Metadata for actions without changes (created, deleted) */}
        {!hasChanges && entry.metadata && Object.keys(entry.metadata).length > 0 && entry.action === 'created' && (
          <div className="mt-2 bg-muted/50 border border-border/50 rounded-md p-2">
            <div className="text-[11px] text-muted-foreground">
              {entry.metadata.equipment_type && <span>Type: {entry.metadata.equipment_type}</span>}
              {entry.metadata.start_date && <span className="ml-3">Date: {entry.metadata.start_date}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function JobActivityLog({ jobId }) {
  const { api } = useAuth();
  const { fmtDateTime } = useTenantSettings();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLog = useCallback(async () => {
    if (!jobId || !api) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/jobs/${jobId}/audit-log?limit=100`);
      setEntries(res.data || []);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Admin access required to view activity log.');
      } else {
        setError('Failed to load activity log.');
      }
    } finally {
      setLoading(false);
    }
  }, [jobId, api]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground text-sm" data-testid="audit-log-loading">
        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
        Loading activity log...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-muted-foreground text-sm" data-testid="audit-log-error">
        {error}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-center py-12" data-testid="audit-log-empty">
        <Clock className="h-8 w-8 text-muted-foreground/40 mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">No activity recorded yet</p>
        <p className="text-xs text-muted-foreground/70 mt-1">Changes to this job will appear here</p>
      </div>
    );
  }

  return (
    <div data-testid="job-activity-log">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Activity Log</h3>
          <p className="text-[11px] text-muted-foreground">{entries.length} event{entries.length !== 1 ? 's' : ''}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchLog} className="h-7 px-2 text-muted-foreground hover:text-foreground">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>
      <ScrollArea className="max-h-[60vh] pr-2">
        <div className="space-y-0">
          {entries.map((entry, index) => (
            <AuditEntry
              key={entry.id}
              entry={entry}
              isLast={index === entries.length - 1}
              fmtDateTime={fmtDateTime}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

export default JobActivityLog;
